import React, { useState, useEffect } from "react";
import axios from "axios";
import { Icon } from "./Icon";

interface Summary {
  id: string;
  date: string;
  summary: string;
  topics: string[];
  participants: Array<{
    username: string;
    messageCount: number;
    sentiment: string;
  }>;
  messageCount: number;
  sentiment?: {
    positive: number;
    negative: number;
    neutral: number;
    questions: number;
  };
  streams?: Array<{
    title: string;
    startTime: string;
    endTime?: string;
  }>;
}

interface ConversationSummaryProps {
  authToken: string;
  userType?: "user" | "premium" | "vip" | "admin";
}

const ConversationSummary: React.FC<ConversationSummaryProps> = ({
  authToken,
}) => {
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryDates, setSummaryDates] = useState<string[]>([]);
  const [messageDates, setMessageDates] = useState<string[]>([]);
  const [earliestDate, setEarliestDate] = useState<Date | null>(null);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userTimezone, setUserTimezone] = useState<string>("UTC");

  useEffect(() => {
    fetchSummaryDates();
    fetchMessageDates();
    detectUserTimezone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Log for debugging
  useEffect(() => {
    console.log("Summary dates loaded:", summaryDates);
  }, [summaryDates]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      if (summaryDates.includes(selectedDate)) {
        loadSummary(selectedDate);
      } else {
        setSummary(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, summaryDates]);

  const fetchSummaryDates = async () => {
    setLoadingDates(true);
    try {
      const response = await axios.get(`/api/bot/summaries/dates`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const dates = response.data as string[];
      setSummaryDates(dates);

      // Find the earliest date and set the most recent as selected
      if (dates.length > 0) {
        const sortedDates = dates.sort();
        const earliest = new Date(sortedDates[0]);
        setEarliestDate(earliest);

        // Select the most recent date with a summary
        const mostRecentDate = sortedDates[sortedDates.length - 1];
        setSelectedDate(mostRecentDate);

        // Update the year/month selectors to show the most recent date
        const recentDate = new Date(mostRecentDate);
        setSelectedYear(recentDate.getFullYear());
        setSelectedMonth(recentDate.getMonth());
      }
    } catch (error) {
      console.error("Error fetching summary dates:", error);
    } finally {
      setLoadingDates(false);
    }
  };

  const detectUserTimezone = () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    setUserTimezone(tz);
  };

  const fetchMessageDates = async () => {
    try {
      const response = await axios.get(`/api/bot/messages/dates`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const dates = response.data as string[];
      setMessageDates(dates);
    } catch (error) {
      console.error("Error fetching message dates:", error);
    }
  };

  const generateSummary = async (date: string) => {
    setGeneratingSummary(true);
    try {
      const response = await axios.post(
        `/api/bot/summaries`,
        { date },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      if (response.data.success) {
        // Add the date to summaryDates immediately
        setSummaryDates((prev) => {
          const updated = [...prev, date];
          return Array.from(new Set(updated)).sort(); // Remove duplicates and sort
        });
        // Load the new summary and wait for it to complete
        await loadSummary(date);
        // Also refresh the dates list in case there are other changes
        fetchSummaryDates();
      }
    } catch (error) {
      console.error("Error generating summary:", error);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const getHoursUntilMidnight = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const hoursLeft = Math.ceil(
      (midnight.getTime() - now.getTime()) / (1000 * 60 * 60),
    );
    return hoursLeft;
  };

  const loadSummary = async (date: string) => {
    setLoadingSummary(true);
    try {
      const response = await axios.get(`/api/bot/summaries`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          startDate: date,
          endDate: date,
          limit: 1,
        },
      });
      if (response.data && response.data.length > 0) {
        setSummary(response.data[0]);
      }
    } catch (error) {
      console.error("Error loading summary:", error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return "#10B981";
      case "negative":
        return "#EF4444";
      case "question":
        return "#3B82F6";
      default:
        return "#6B7280";
    }
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDateWithDayOfWeek = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00");
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const handleDateClick = (day: number) => {
    const date = new Date(selectedYear, selectedMonth, day);
    // Use local date string to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const dayStr = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${dayStr}`;

    // Allow clicking on dates with summaries or messages
    if (summaryDates.includes(dateStr) || messageDates.includes(dateStr)) {
      setSelectedDate(dateStr);
    }
  };

  // Removed changeMonth - no longer needed with flat selector

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth, day);
      // Use local date string to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const dayStr = String(date.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${dayStr}`;
      const hasSummary = summaryDates.includes(dateStr);
      const hasMessages = messageDates.includes(dateStr);
      const isClickable = hasSummary || hasMessages;
      const isSelected = dateStr === selectedDate;
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const isToday = dateStr === todayStr;
      const isFuture = date > new Date();

      days.push(
        <div
          key={day}
          onClick={() => isClickable && !isFuture && handleDateClick(day)}
          style={{
            padding: "6px",
            textAlign: "center",
            borderRadius: "8px",
            cursor: isClickable && !isFuture ? "pointer" : "not-allowed",
            backgroundColor: isSelected
              ? "#9146FF"
              : hasSummary
                ? "#374151"
                : hasMessages
                  ? "#4B5563"
                  : "transparent",
            color: !isClickable || isFuture ? "#4a5568" : "#e2e8f0",
            border: isToday ? "2px solid #9146FF" : "2px solid transparent",
            fontWeight: isToday ? "bold" : "normal",
            transition: "all 0.2s",
            position: "relative",
            opacity: !hasMessages && !hasSummary && !isToday ? 0.5 : 1,
          }}
          onMouseEnter={(e) => {
            if (isClickable && !isSelected && !isFuture) {
              e.currentTarget.style.backgroundColor = "#4a5568";
            }
          }}
          onMouseLeave={(e) => {
            if (isClickable && !isSelected && !isFuture) {
              e.currentTarget.style.backgroundColor = hasSummary
                ? "#374151"
                : "#4B5563";
            }
          }}
        >
          {day}
          {(hasSummary || hasMessages) && (
            <div
              style={{
                position: "absolute",
                bottom: "2px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "4px",
                height: "4px",
                backgroundColor: hasSummary ? "#10B981" : "#F59E0B",
                borderRadius: "50%",
              }}
            />
          )}
        </div>,
      );
    }

    return days;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentYear = new Date().getFullYear();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          flex: "1",
          maxWidth: isMobile ? "100%" : "800px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}
        >
          Daily Conversation Summary
        </h3>

        {/* Calendar and Overview Container */}
        <div
          style={{
            display: isMobile ? "block" : "flex",
            gap: "20px",
            marginBottom: "20px",
            alignItems: "stretch",
          }}
        >
          {/* Calendar */}
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              width: isMobile ? "100%" : "calc(50% - 10px)",
              flexShrink: 0,
            }}
          >
            {loadingDates ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#718096",
                }}
              >
                Loading calendar...
              </div>
            ) : (
              <>
                {/* Month/Year Selector */}
                <div
                  style={{
                    marginBottom: "12px",
                  }}
                >
                  {/* Year selector */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "8px",
                      marginBottom: "10px",
                    }}
                  >
                    {earliestDate &&
                      Array.from(
                        {
                          length: currentYear - earliestDate.getFullYear() + 1,
                        },
                        (_, i) => earliestDate.getFullYear() + i,
                      ).map((year) => (
                        <button
                          key={year}
                          onClick={() => setSelectedYear(year)}
                          style={{
                            padding: "4px 10px",
                            backgroundColor:
                              selectedYear === year ? "#9146FF" : "transparent",
                            border:
                              selectedYear === year
                                ? "none"
                                : "1px solid #4a5568",
                            borderRadius: "4px",
                            color: selectedYear === year ? "#fff" : "#e2e8f0",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight:
                              selectedYear === year ? "bold" : "normal",
                            transition: "all 0.2s",
                          }}
                        >
                          {year}
                        </button>
                      ))}
                  </div>
                  {/* Month selector */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: "8px",
                    }}
                  >
                    {monthNames.map((month, index) => {
                      const isDisabled = earliestDate
                        ? (selectedYear === earliestDate.getFullYear() &&
                            index < earliestDate.getMonth()) ||
                          (selectedYear === currentYear &&
                            index > new Date().getMonth())
                        : false;
                      return (
                        <button
                          key={index}
                          onClick={() => !isDisabled && setSelectedMonth(index)}
                          disabled={isDisabled}
                          style={{
                            padding: "6px",
                            backgroundColor:
                              selectedMonth === index
                                ? "#9146FF"
                                : "transparent",
                            border:
                              selectedMonth === index
                                ? "none"
                                : "1px solid #4a5568",
                            borderRadius: "4px",
                            color: isDisabled
                              ? "#4a5568"
                              : selectedMonth === index
                                ? "#fff"
                                : "#e2e8f0",
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            fontSize: "13px",
                            fontWeight:
                              selectedMonth === index ? "bold" : "normal",
                            transition: "all 0.2s",
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          {month.slice(0, 3)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Calendar Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "2px",
                    marginBottom: "4px",
                  }}
                >
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                    <div
                      key={day}
                      style={{
                        padding: "4px",
                        textAlign: "center",
                        fontSize: "12px",
                        color: "#718096",
                        fontWeight: "bold",
                      }}
                    >
                      {day}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "2px",
                  }}
                >
                  {renderCalendar()}
                </div>
              </>
            )}
          </div>

          {/* Overview Section - Shows next to calendar */}
          {!isMobile && (summary || selectedDate) && (
            <div
              style={{
                backgroundColor: "#2d3748",
                padding: "20px",
                borderRadius: "8px",
                border: "1px solid #4a5568",
                boxSizing: "border-box",
                width: "calc(50% - 10px)",
                display: "flex",
                flexDirection: "column",
                minHeight: "400px",
              }}
            >
              {summary ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <h4
                      style={{
                        fontWeight: "600",
                        margin: 0,
                        color: "#e2e8f0",
                      }}
                    >
                      Overview
                    </h4>
                    <span
                      style={{
                        color: "#9CA3AF",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {formatDateWithDayOfWeek(summary.date)}
                    </span>
                  </div>
                  <p style={{ color: "#cbd5e0", lineHeight: 1.6, flex: 1 }}>
                    {summary.summary}
                  </p>
                  <div
                    style={{
                      marginTop: "16px",
                      fontSize: "14px",
                      color: "#cbd5e0",
                    }}
                  >
                    Total Messages: {summary.messageCount}
                  </div>
                </>
              ) : selectedDate ? (
                // Show today's summary countdown or other status
                (() => {
                  const today = new Date();
                  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
                  const isToday = selectedDate === todayStr;
                  const hasMessages = messageDates.includes(selectedDate);
                  const hasSummary = summaryDates.includes(selectedDate);

                  if (isToday) {
                    const hoursLeft = getHoursUntilMidnight();
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        <h4
                          style={{
                            fontWeight: "600",
                            margin: "0 0 24px 0",
                            color: "#e2e8f0",
                            fontSize: "18px",
                          }}
                        >
                          Today&apos;s Summary
                        </h4>
                        <p
                          style={{
                            margin: "0 0 24px 0",
                            fontSize: "16px",
                            color: "#cbd5e0",
                            lineHeight: 1.6,
                          }}
                        >
                          Daily summaries are automatically generated at
                          midnight in your timezone ({userTimezone}).
                        </p>
                        <div
                          style={{
                            backgroundColor: "#374151",
                            padding: "16px 24px",
                            borderRadius: "8px",
                            display: "inline-block",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontSize: "24px",
                              fontWeight: "bold",
                              color: "#10B981",
                            }}
                          >
                            {hoursLeft} {hoursLeft === 1 ? "hour" : "hours"}{" "}
                            until summary generation
                          </p>
                        </div>
                      </div>
                    );
                  } else if (hasMessages && !hasSummary) {
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        <h4
                          style={{
                            fontWeight: "600",
                            margin: "0 0 16px 0",
                            color: "#F59E0B",
                            fontSize: "18px",
                          }}
                        >
                          Missing Summary
                        </h4>
                        <p
                          style={{
                            margin: "0 0 24px 0",
                            fontSize: "16px",
                            color: "#cbd5e0",
                          }}
                        >
                          Chat messages exist for this date, but no summary has
                          been generated yet.
                        </p>
                        <button
                          onClick={() => generateSummary(selectedDate)}
                          disabled={generatingSummary}
                          style={{
                            padding: "12px 24px",
                            backgroundColor: generatingSummary
                              ? "#4B5563"
                              : "#9146FF",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "16px",
                            fontWeight: "bold",
                            cursor: generatingSummary
                              ? "not-allowed"
                              : "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (!generatingSummary) {
                              e.currentTarget.style.backgroundColor = "#7C3AED";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!generatingSummary) {
                              e.currentTarget.style.backgroundColor = "#9146FF";
                            }
                          }}
                        >
                          {generatingSummary
                            ? "Generating..."
                            : "Generate Summary"}
                        </button>
                      </div>
                    );
                  } else {
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            color: "#a0aec0",
                          }}
                        >
                          No chat messages recorded for this date.
                        </p>
                      </div>
                    );
                  }
                })()
              ) : null}
            </div>
          )}
        </div>

        {/* Rest of Summary Display */}
        {loadingSummary || generatingSummary ? (
          !isMobile && (
            <div
              style={{
                backgroundColor: "#2d3748",
                padding: "40px",
                borderRadius: "8px",
                textAlign: "center",
                color: "#a0aec0",
              }}
            >
              {generatingSummary
                ? "Generating summary..."
                : "Loading summary..."}
            </div>
          )
        ) : summary ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            {/* Overview for mobile - shows below calendar */}
            {isMobile && (
              <div
                style={{
                  backgroundColor: "#2d3748",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #4a5568",
                  boxSizing: "border-box",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <h4
                    style={{
                      fontWeight: "600",
                      margin: 0,
                      color: "#e2e8f0",
                    }}
                  >
                    Overview
                  </h4>
                  <span
                    style={{
                      color: "#9CA3AF",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    {formatDateWithDayOfWeek(summary.date)}
                  </span>
                </div>
                <p style={{ color: "#cbd5e0", lineHeight: 1.6 }}>
                  {summary.summary}
                </p>
                <div
                  style={{
                    marginTop: "16px",
                    fontSize: "14px",
                    color: "#cbd5e0",
                  }}
                >
                  Total Messages: {summary.messageCount}
                </div>
              </div>
            )}

            {/* Stream Information */}
            {summary.streams && summary.streams.length > 0 && (
              <div>
                <h4
                  style={{
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "#e2e8f0",
                  }}
                >
                  Stream Activity
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {summary.streams.map((stream, idx) => {
                    const startTime = new Date(stream.startTime);
                    const timeStr = startTime.toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    });
                    return (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "12px",
                          backgroundColor: "#2d3748",
                          borderRadius: "8px",
                          border: "1px solid #4a5568",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "10px",
                            height: "10px",
                            backgroundColor: "#e53e3e",
                            borderRadius: "50%",
                            flexShrink: 0,
                            boxShadow: "0 0 0 0 rgba(229, 62, 62, 0.7)",
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#e2e8f0",
                              marginBottom: "4px",
                            }}
                          >
                            {stream.title}
                          </div>
                          <div style={{ fontSize: "13px", color: "#a0aec0" }}>
                            Started at {timeStr}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sentiment Analysis */}
            {summary.sentiment && (
              <div>
                <h4
                  style={{
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "#e2e8f0",
                  }}
                >
                  Sentiment Breakdown
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr 1fr"
                      : "1fr 1fr 1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      backgroundColor: "#065F46",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#10B981",
                      }}
                    >
                      {summary.sentiment.positive}
                    </div>
                    <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                      Positive
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      backgroundColor: "#7F1D1D",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#EF4444",
                      }}
                    >
                      {summary.sentiment.negative}
                    </div>
                    <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                      Negative
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      backgroundColor: "#374151",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#6B7280",
                      }}
                    >
                      {summary.sentiment.neutral}
                    </div>
                    <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                      Neutral
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      backgroundColor: "#1E3A8A",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#3B82F6",
                      }}
                    >
                      {summary.sentiment.questions}
                    </div>
                    <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                      Questions
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Participants */}
            {summary.participants && summary.participants.length > 0 && (
              <div>
                <h4
                  style={{
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "#e2e8f0",
                  }}
                >
                  Most Active Users
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {summary.participants
                    .sort((a, b) => b.messageCount - a.messageCount)
                    .slice(0, 5)
                    .map((participant, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px",
                          backgroundColor: "#2d3748",
                          borderRadius: "8px",
                          border: "1px solid #4a5568",
                        }}
                      >
                        <span style={{ fontWeight: "500", color: "#e2e8f0" }}>
                          {participant.username}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                          }}
                        >
                          <span style={{ fontSize: "14px", color: "#cbd5e0" }}>
                            {participant.messageCount} messages
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              color: getSentimentColor(participant.sentiment),
                            }}
                          >
                            {participant.sentiment}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Topics */}
            {summary.topics && summary.topics.length > 0 && (
              <div>
                <h4
                  style={{
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "#e2e8f0",
                  }}
                >
                  Topics Discussed
                </h4>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {summary.topics.map((topic, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: "6px 16px",
                        backgroundColor: "#DBEAFE",
                        color: "#1E40AF",
                        borderRadius: "20px",
                        fontSize: "14px",
                      }}
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : selectedDate && isMobile ? (
          // On mobile, show the status below the calendar since overview panel is not visible
          (() => {
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
            const isToday = selectedDate === todayStr;
            const hasMessages = messageDates.includes(selectedDate);
            const hasSummary = summaryDates.includes(selectedDate);

            if (isToday) {
              // Show countdown for today
              const hoursLeft = getHoursUntilMidnight();
              return (
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "40px",
                    borderRadius: "8px",
                    textAlign: "center",
                    color: "#e2e8f0",
                  }}
                >
                  <h4
                    style={{
                      marginTop: 0,
                      marginBottom: "16px",
                      color: "#9146FF",
                    }}
                  >
                    Today&apos;s Summary
                  </h4>
                  <p style={{ margin: "0 0 16px 0", fontSize: "16px" }}>
                    Daily summaries are automatically generated at midnight in
                    your timezone ({userTimezone}).
                  </p>
                  <div
                    style={{
                      backgroundColor: "#374151",
                      padding: "16px",
                      borderRadius: "8px",
                      display: "inline-block",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#10B981",
                      }}
                    >
                      {hoursLeft} {hoursLeft === 1 ? "hour" : "hours"} until
                      summary generation
                    </p>
                  </div>
                </div>
              );
            } else if (hasMessages && !hasSummary) {
              // Show generate button for days with messages but no summary
              return (
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "40px",
                    borderRadius: "8px",
                    textAlign: "center",
                    color: "#e2e8f0",
                  }}
                >
                  <h4
                    style={{
                      marginTop: 0,
                      marginBottom: "16px",
                      color: "#F59E0B",
                    }}
                  >
                    Missing Summary
                  </h4>
                  <p style={{ margin: "0 0 24px 0", fontSize: "16px" }}>
                    Chat messages exist for this date, but no summary has been
                    generated yet.
                  </p>
                  <button
                    onClick={() => generateSummary(selectedDate)}
                    disabled={generatingSummary}
                    style={{
                      padding: "12px 24px",
                      backgroundColor: generatingSummary
                        ? "#4B5563"
                        : "#9146FF",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      cursor: generatingSummary ? "not-allowed" : "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (!generatingSummary) {
                        e.currentTarget.style.backgroundColor = "#7C3AED";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!generatingSummary) {
                        e.currentTarget.style.backgroundColor = "#9146FF";
                      }
                    }}
                  >
                    {generatingSummary ? "Generating..." : "Generate Summary"}
                  </button>
                </div>
              );
            } else if (!hasMessages) {
              // No messages for this date
              return (
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "40px",
                    borderRadius: "8px",
                    textAlign: "center",
                    color: "#a0aec0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "16px" }}>
                    No chat messages recorded for this date.
                  </p>
                </div>
              );
            } else {
              // Has summary but loading failed
              return (
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "40px",
                    borderRadius: "8px",
                    textAlign: "center",
                    color: "#a0aec0",
                  }}
                >
                  No summary data available for this date.
                </div>
              );
            }
          })()
        ) : (
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "40px",
              borderRadius: "8px",
              textAlign: "center",
              color: "#a0aec0",
            }}
          >
            {loadingDates ? (
              <p style={{ margin: 0, fontSize: "16px" }}>
                Loading summaries...
              </p>
            ) : (
              <>
                <p style={{ margin: 0, fontSize: "16px" }}>
                  Select a highlighted date to view its summary.
                </p>
                <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>
                  <Icon
                    name="circle"
                    size={12}
                    color="#10B981"
                    style={{ verticalAlign: "-1px", marginRight: "8px" }}
                  />
                  Green dots = Summary generated
                </p>
                <p style={{ margin: "4px 0 0 0", fontSize: "14px" }}>
                  <Icon
                    name="circle"
                    size={12}
                    color="#F59E0B"
                    style={{ verticalAlign: "-1px", marginRight: "8px" }}
                  />
                  Orange dots = Messages exist, no summary
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Summaries Info Panel */}
      <div
        style={{
          width: isMobile ? "100%" : "350px",
          maxWidth: isMobile ? "800px" : "350px",
          backgroundColor: "rgba(45, 55, 72, 0.95)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          borderRadius: "8px",
          padding: "20px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          height: "fit-content",
          position: "relative",
          order: isMobile ? 2 : 0,
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{
            color: "#e2e8f0",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "18px",
          }}
        >
          <Icon
            name="bar-chart-3"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Summaries Guide
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="bot"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            What are Summaries?
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            AI-generated daily reports that analyze your chat activity,
            identifying key topics, sentiment, and engaged users.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="trending-up"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Summary Features
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Overview</strong> - Daily activity summary
            </li>
            <li>
              <strong>Topics</strong> - Main discussion themes
            </li>
            <li>
              <strong>Sentiment</strong> - Mood analysis
            </li>
            <li>
              <strong>Top Users</strong> - Most active participants
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="lightbulb"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            How to Use
          </h4>
          <ol
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>Navigate to any month using the controls</li>
            <li>Click on days with green dots</li>
            <li>View detailed insights for that day</li>
            <li>Track patterns over time</li>
          </ol>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="moon"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Automatic Generation
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Premium users get daily summaries automatically generated at
            midnight in your timezone. No action needed!
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="calendar"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Calendar Legend
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <Icon
                name="circle"
                size={12}
                color="#10B981"
                style={{ verticalAlign: "-1px", marginRight: "6px" }}
              />
              <strong>Green Dot</strong> - Summary available
            </li>
            <li>
              <Icon
                name="circle"
                size={12}
                color="#F59E0B"
                style={{ verticalAlign: "-1px", marginRight: "6px" }}
              />
              <strong>Orange Dot</strong> - Messages exist, no summary
            </li>
            <li>
              <Icon
                name="circle"
                size={12}
                color="#9146FF"
                style={{ verticalAlign: "-1px", marginRight: "6px" }}
              />
              <strong>Purple Background</strong> - Selected date
            </li>
            <li>
              <Icon
                name="square"
                size={12}
                color="#4a5568"
                style={{ verticalAlign: "-1px", marginRight: "6px" }}
              />
              <strong>Gray/Faded</strong> - No messages for this date
            </li>
          </ul>
        </div>

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #4a5568",
          }}
        >
          <p
            style={{
              color: "#9146FF",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            <Icon
              name="star"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Premium Feature
          </p>
          <p style={{ color: "#a0aec0", fontSize: "11px", margin: 0 }}>
            Daily summaries help you track chat trends and community engagement
            over time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConversationSummary;
